import { useState, useMemo } from "react";
import { useMacroData } from "@/hooks/useMacroData";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  RefreshCw, TrendingUp, TrendingDown, Minus, DollarSign, 
  Percent, Activity, BarChart3, AlertTriangle,
  Building2, Wallet, Landmark, ChartLine, GitCompareArrows,
  Settings, BookOpen, ChevronDown, Loader2
} from "lucide-react";
import { 
  ResponsiveContainer, ComposedChart, Area, Line, 
  XAxis, YAxis, Tooltip, Legend 
} from "recharts";
import { format, subYears, parseISO, getISOWeek, getISOWeekYear } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Fetch BTC price history - FIXED: use correct parameter and data access
async function fetchBTCHistory(period: string = '5Y'): Promise<Array<{ date: string; price: number }>> {
  console.log(`[MacroIndicators] Fetching BTC history for period: ${period}`);
  const { data, error } = await supabase.functions.invoke('btc-history', {
    body: { period }
  });
  if (error) {
    console.error('[MacroIndicators] BTC history error:', error);
    throw error;
  }
  console.log(`[MacroIndicators] BTC history received: ${data?.length || 0} points`);
  return data || [];
}

// Period options
const PERIOD_OPTIONS = [
  { label: '1Y', years: 1 },
  { label: '2Y', years: 2 },
  { label: '3Y', years: 3 },
  { label: '5Y', years: 5 },
  { label: '10Y', years: 10 },
];

type UnitType = 'usd' | 'percent' | 'index' | 'bps';

const INDICATOR_UNITS: Record<string, { unit: UnitType; transform?: (v: number) => number }> = {
  // Values in MacroData are mostly in their native FRED units. We transform here so axis/tooltip match the card logic.
  m2: { unit: 'usd', transform: (v) => v * 1e9 }, // M2 is in billions
  fedBalance: { unit: 'usd', transform: (v) => v * 1e6 }, // WALCL is in millions
  rrp: { unit: 'usd', transform: (v) => v * 1e9 }, // RRP is in billions
  netLiquidity: { unit: 'usd' }, // already in USD
  iorb: { unit: 'percent' },
  stlFsi: { unit: 'index' },
  tga: { unit: 'usd', transform: (v) => v * 1e9 }, // WTREGEN appears to be in billions
  // Spread in percentage points -> show as bps
  sofrIorb: { unit: 'bps', transform: (v) => v * 100 },
};

// Indicator definitions
const INDICATORS: Record<string, {
  key: string;
  label: string;
  fullName: string;
  color: string;
  icon: any;
  description: string;
  rising: string;
  falling: string;
  correlation: string;
}> = {
  m2: {
    key: 'm2',
    label: 'M2',
    fullName: 'M2 Money Supply',
    color: 'hsl(210, 100%, 60%)',
    icon: DollarSign,
    description: 'Total de dinheiro em circulação na economia (cash + depósitos bancários + money markets)',
    rising: 'Mais liquidez no sistema = suporte para ativos de risco como BTC e crypto',
    falling: 'Contração monetária = pressão vendedora em ativos de risco',
    correlation: 'Alta (~0.85 com lag de 3-6 meses)',
  },
  fedBalance: {
    key: 'fedBalance',
    label: 'Fed Balance',
    fullName: 'Fed Balance Sheet (WALCL)',
    color: 'hsl(142, 76%, 50%)',
    icon: Building2,
    description: 'Total de ativos no balanço do Federal Reserve. Mede a política monetária direta do Fed.',
    rising: 'Quantitative Easing (QE) - Fed comprando ativos = injeção de liquidez = bullish para crypto',
    falling: 'Quantitative Tightening (QT) - Fed vendendo ativos = drenagem de liquidez = headwind para risco',
    correlation: 'Alta - principal driver de liquidez global',
  },
  rrp: {
    key: 'rrp',
    label: 'RRP',
    fullName: 'Overnight Reverse Repo (RRPONTSYD)',
    color: 'hsl(0, 84%, 60%)',
    icon: Wallet,
    description: 'Facilidade onde instituições "estacionam" excesso de liquidez overnight no Fed.',
    rising: 'Dinheiro saindo do sistema para o Fed = menos liquidez disponível para investir',
    falling: 'Dinheiro voltando ao sistema = mais capital disponível para ativos de risco',
    correlation: 'Inversa com Net Liquidity - RRP alto = menos liquidez efetiva',
  },
  netLiquidity: {
    key: 'netLiquidity',
    label: 'Net Liquidity',
    fullName: 'Fed Net Liquidity',
    color: 'hsl(45, 100%, 50%)',
    icon: ChartLine,
    description: 'Liquidez efetiva = Fed Balance Sheet - RRP. Mede quanto dinheiro está realmente disponível.',
    rising: 'Mais dinheiro disponível para investir em ativos = ambiente favorável para crypto',
    falling: 'Menos dinheiro no sistema = ambiente desafiador para ativos de risco',
    correlation: 'Muito alta com BTC (~0.90) - principal indicador macro para crypto',
  },
  iorb: {
    key: 'iorb',
    label: 'IORB',
    fullName: 'Interest on Reserve Balances',
    color: 'hsl(270, 70%, 60%)',
    icon: Percent,
    description: 'Taxa de juros que o Fed paga aos bancos por reservas depositadas. Ferramenta de política monetária.',
    rising: 'Fed mais hawkish / taxas mais altas = pressão em ativos de risco / crypto',
    falling: 'Fed mais dovish / taxas caindo = ambiente mais favorável para risco',
    correlation: 'Inversa - taxas altas historicamente pressionam BTC',
  },
  stlFsi: {
    key: 'stlFsi',
    label: 'STL FSI',
    fullName: 'St. Louis Fed Financial Stress Index',
    color: 'hsl(25, 100%, 55%)',
    icon: AlertTriangle,
    description: 'Índice de stress financeiro. Valores abaixo de 0 = condições normais. Acima de 0 = stress elevado.',
    rising: 'Stress no sistema financeiro = ambiente de risk-off / sell-offs em crypto',
    falling: 'Condições financeiras relaxando = ambiente mais favorável para risco',
    correlation: 'Inversa - stress alto correlaciona com quedas em BTC',
  },
  tga: {
    key: 'tga',
    label: 'TGA',
    fullName: 'Treasury General Account',
    color: 'hsl(180, 70%, 45%)',
    icon: Landmark,
    description: 'Conta do Tesouro no Fed. Quando o governo gasta dessa conta, liquidez entra no sistema.',
    rising: 'Governo acumulando dinheiro = liquidez sendo retirada do sistema',
    falling: 'Governo gastando = injeção de liquidez no mercado = positivo para risco',
    correlation: 'Inversa moderada - gastos do TGA suportam crypto',
  },
  sofrIorb: {
    key: 'sofrIorb',
    label: 'SOFR-IORB',
    fullName: 'Spread SOFR vs IORB',
    color: 'hsl(320, 70%, 55%)',
    icon: GitCompareArrows,
    description: 'Diferença entre SOFR (taxa repo) e IORB (taxa de reservas). Mede pressão de liquidez no sistema.',
    rising: 'SOFR acima de IORB = "teto vazando" = stress de liquidez interbancária',
    falling: 'SOFR abaixo de IORB = sistema funcionando normalmente',
    correlation: 'Inversa - spread alto = risco de correção em ativos',
  },
};

// Format utilities
function formatCurrency(value: number | null, scale: 'T' | 'B' | 'M' = 'B'): string {
  if (value === null || value === undefined) return 'N/A';
  const divisor = scale === 'T' ? 1e12 : scale === 'B' ? 1e9 : 1e6;
  const formatted = (value / divisor).toFixed(2);
  return `$${formatted}${scale}`;
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatRate(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(2)}%`;
}

function formatIndex(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(3)}`;
}

function formatBps(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  const bps = value * 100;
  const sign = bps > 0 ? '+' : '';
  return `${sign}${bps.toFixed(1)} bps`;
}

// Convert date to ISO week key (e.g., "2024-W03")
function toISOWeek(dateStr: string): string {
  try {
    const d = parseISO(dateStr);
    const week = getISOWeek(d);
    const year = getISOWeekYear(d);
    return `${year}-W${String(week).padStart(2, '0')}`;
  } catch {
    return dateStr;
  }
}

// Aggregate data by ISO week (take last value of each week)
function aggregateToWeekly(history: Array<{ date: string; value: number | null }>): Map<string, { date: string; value: number }> {
  const weekMap = new Map<string, { date: string; value: number }>();
  
  const sorted = [...history]
    .filter(h => h.value !== null && h.value !== undefined && !isNaN(h.value))
    .sort((a, b) => a.date.localeCompare(b.date));
  
  for (const h of sorted) {
    const weekKey = toISOWeek(h.date);
    weekMap.set(weekKey, { date: h.date, value: h.value! });
  }
  
  return weekMap;
}

// Indicator toggle row component
function IndicatorToggleRow({ 
  indicator, 
  isActive, 
  onToggle 
}: { 
  indicator: typeof INDICATORS[string]; 
  isActive: boolean; 
  onToggle: () => void;
}) {
  const Icon = indicator.icon;
  return (
    <button
      onClick={onToggle}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
        isActive 
          ? "border-primary/50 bg-primary/10" 
          : "border-border/50 bg-card/50 hover:border-primary/30 hover:bg-muted/50"
      )}
    >
      <div 
        className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${indicator.color}20` }}
      >
        <Icon className="h-4 w-4" style={{ color: indicator.color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{indicator.label}</div>
        <div className="text-xs text-muted-foreground truncate">{indicator.fullName}</div>
      </div>
      {isActive && (
        <div 
          className="h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: indicator.color }}
        />
      )}
    </button>
  );
}

export default function MacroIndicators() {
  const { data: macroData, isLoading, forceRefresh } = useMacroData();
  const [selectedPeriod, setSelectedPeriod] = useState(2);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState<string>('netLiquidity');
  const [educationOpen, setEducationOpen] = useState(false);

  // Fixed: pass period dynamically
  const { data: btcHistory, isLoading: btcLoading, refetch: refetchBtc } = useQuery({
    queryKey: ['btc-history-macro', selectedPeriod],
    queryFn: () => fetchBTCHistory(`${selectedPeriod}Y`),
    staleTime: 30 * 60 * 1000,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        forceRefresh(),
        refetchBtc(),
      ]);
      toast.success('Dados atualizados com sucesso!');
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Erro ao atualizar dados. Tente novamente.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleIndicator = (key: string) => {
    // BTC + 1 indicador: trocar o atual pelo novo
    setSelectedIndicator(key);
  };

  // Get indicator raw data
  const getIndicatorHistory = (key: string): Array<{ date: string; value: number | null }> => {
    if (!macroData) return [];
    
    // Special case for SOFR-IORB spread (calculated)
    if (key === 'sofrIorb') {
      const sofrHistory = macroData.sofr?.history || [];
      const iorbHistory = macroData.iorb?.history || [];
      
      if (!sofrHistory.length || !iorbHistory.length) return [];
      
      const iorbMap = new Map(iorbHistory.map(h => [h.date, h.value]));
      
      return sofrHistory
        .map(s => {
          const iorbVal = iorbMap.get(s.date);
          if (s.value === null || iorbVal === null || iorbVal === undefined) {
            return { date: s.date, value: null };
          }
          return { date: s.date, value: s.value - iorbVal };
        })
        .filter(h => h.value !== null);
    }
    
    const indicatorMap: Record<string, any> = {
      m2: macroData.m2,
      fedBalance: macroData.fedBalance,
      rrp: macroData.rrp,
      netLiquidity: macroData.netLiquidity,
      stlFsi: macroData.stlFsi,
      iorb: macroData.iorb,
      tga: macroData.tga,
    };
    return indicatorMap[key]?.history || [];
  };

  // Combined chart data using ISO week merge
  const chartData = useMemo(() => {
    if (!btcHistory?.length) return [];
    
    const cutoffDate = subYears(new Date(), selectedPeriod);
    
    const btcFiltered = btcHistory.filter(b => {
      try {
        return parseISO(b.date) >= cutoffDate;
      } catch {
        return false;
      }
    });
    
    if (!btcFiltered.length) return [];
    
    const btcWeekly = aggregateToWeekly(
      btcFiltered.map(b => ({ date: b.date, value: b.price }))
    );
    
    const btcWeeks = Array.from(btcWeekly.keys()).sort();
    
    if (!btcWeeks.length) return [];
    
    const indicatorKey = selectedIndicator;
    const history = getIndicatorHistory(indicatorKey);
    const weeklyMap = history.length ? aggregateToWeekly(history) : new Map<string, { date: string; value: number }>();
    
    const result: Array<Record<string, any>> = [];
    let lastKnownValue: number | undefined;
    
    for (const week of btcWeeks) {
      const btcData = btcWeekly.get(week);
      if (!btcData) continue;
      
      const point: Record<string, any> = { 
        date: btcData.date, 
        week,
        btcPrice: btcData.value 
      };
      
      const weekData = weeklyMap.get(week);
      let rawValue: number | undefined;

      if (weekData) {
        rawValue = weekData.value;
        lastKnownValue = rawValue;
      } else {
        rawValue = lastKnownValue;
      }

      if (rawValue !== undefined) {
        const meta = INDICATOR_UNITS[indicatorKey];
        const transformed = meta?.transform ? meta.transform(rawValue) : rawValue;
        point.indicatorKey = indicatorKey;
        point.indicatorRaw = rawValue;
        point.indicatorValue = transformed;
      }
      
      result.push(point);
    }
    
    return result;
  }, [btcHistory, macroData, selectedIndicator, selectedPeriod]);

  // Calculate BTC Y-axis domain
  const btcDomain = useMemo(() => {
    if (!chartData.length) return [0, 100000];
    const prices = chartData.map(d => d.btcPrice).filter(Boolean);
    if (!prices.length) return [0, 100000];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return [min * 0.9, max * 1.1];
  }, [chartData]);

  // Format values for display
  const getMetricValue = (key: string): { value: string; change: string | null; changeValue: number | null } => {
    if (!macroData) return { value: 'N/A', change: null, changeValue: null };
    
    switch (key) {
      case 'm2':
        return {
          value: formatCurrency(macroData.m2?.current ? macroData.m2.current * 1e9 : null, 'T'),
          change: formatPercent(macroData.m2?.changeYoYPct ?? null),
          changeValue: macroData.m2?.changeYoYPct ?? null,
        };
      case 'fedBalance':
        return {
          value: formatCurrency(macroData.fedBalance?.current ? macroData.fedBalance.current * 1e6 : null, 'T'),
          change: formatPercent(macroData.fedBalance?.change30dPct ?? null),
          changeValue: macroData.fedBalance?.change30dPct ?? null,
        };
      case 'rrp':
        return {
          value: formatCurrency(macroData.rrp?.current ? macroData.rrp.current * 1e9 : null, 'B'),
          change: formatPercent(macroData.rrp?.change30dPct ?? null),
          changeValue: macroData.rrp?.change30dPct ?? null,
        };
      case 'netLiquidity':
        return {
          value: formatCurrency(macroData.netLiquidity?.current, 'T'),
          change: formatPercent(macroData.netLiquidity?.change30dPct ?? null),
          changeValue: macroData.netLiquidity?.change30dPct ?? null,
        };
      case 'iorb':
        return {
          value: formatRate(macroData.iorb?.current ?? null),
          // change7d comes in percentage points; display in bps
          change: macroData.iorb?.change7d != null ? `${macroData.iorb.change7d > 0 ? '+' : ''}${(macroData.iorb.change7d * 100).toFixed(1)} bps` : null,
          changeValue: macroData.iorb?.change7d != null ? macroData.iorb.change7d * 100 : null,
        };
      case 'stlFsi':
        return {
          value: formatIndex(macroData.stlFsi?.current ?? null),
          change: formatPercent(macroData.stlFsi?.change30dPct ?? null),
          changeValue: macroData.stlFsi?.change30dPct != null ? -macroData.stlFsi.change30dPct : null,
        };
      case 'tga':
        return {
          value: formatCurrency(macroData.tga?.current ? macroData.tga.current * 1e9 : null, 'B'),
          change: formatPercent(macroData.tga?.change30dPct ?? null),
          changeValue: macroData.tga?.change30dPct ?? null,
        };
      case 'sofrIorb': {
        const sofr = macroData.sofr?.current;
        const iorb = macroData.iorb?.current;
        const spread = sofr !== null && sofr !== undefined && iorb !== null && iorb !== undefined
          ? sofr - iorb
          : null;
        return {
          value: formatBps(spread),
          change: null,
          changeValue: spread !== null ? (spread > 0 ? 1 : spread < 0 ? -1 : 0) : null,
        };
      }
      default:
        return { value: 'N/A', change: null, changeValue: null };
    }
  };

  // Available indicators (filter out ones without data)
  const availableIndicators = Object.keys(INDICATORS).filter(key => {
    if (key === 'tga') return !!macroData?.tga?.history?.length;
    if (key === 'sofrIorb') return !!macroData?.sofr?.history?.length && !!macroData?.iorb?.history?.length;
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none opacity-30" />
        <div className="fixed top-20 left-1/4 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="fixed bottom-20 right-1/4 w-[300px] h-[300px] bg-chart-2/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="container mx-auto px-4 py-8 space-y-8 relative z-10">
          <div className="flex items-center justify-between">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Skeleton className="lg:col-span-3 h-[500px]" />
            <Skeleton className="h-[500px]" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none opacity-30" />
      <div className="fixed top-20 left-1/4 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-20 right-1/4 w-[300px] h-[300px] bg-chart-2/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 py-8 space-y-8 relative z-10">
        {/* Hero Header */}
        <div className="animate-fade-in space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-4 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Indicadores Macro
                </h1>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                  <Activity className="h-3 w-3 mr-1" />
                  {availableIndicators.length} Indicadores
                </Badge>
                <Badge variant="secondary" className="bg-chart-2/10 text-chart-2 border-chart-2/20">
                  1 Selecionado
                </Badge>
              </div>
              <p className="text-muted-foreground">
                Análise de liquidez global e correlação com Bitcoin
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Period selector pills */}
              <div className="flex items-center gap-1 bg-muted/50 backdrop-blur-sm rounded-lg p-1 border border-border/50">
                {PERIOD_OPTIONS.map((option) => (
                  <Button
                    key={option.years}
                    variant={selectedPeriod === option.years ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSelectedPeriod(option.years)}
                    className={cn(
                      "px-4 h-8 transition-all",
                      selectedPeriod === option.years && "shadow-md"
                    )}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="gap-2 border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card"
              >
                {isRefreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Atualizar
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content - Chart + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
          {/* Chart - 3 columns */}
          <Card className="lg:col-span-3 border-border/50 bg-card/50 backdrop-blur-sm shadow-xl">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">BTC vs Indicadores Macro</CardTitle>
                  <CardDescription>
                    Comparando: {INDICATORS[selectedIndicator]?.label}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[500px]">
                {btcLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                      <BarChart3 className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      Carregando Dados...
                    </h3>
                    <p className="text-muted-foreground text-sm max-w-sm">
                      Aguarde enquanto os dados são carregados...
                    </p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="btcGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(d) => {
                          try {
                            return format(parseISO(d), 'MMM yy');
                          } catch {
                            return d;
                          }
                        }}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        minTickGap={50}
                      />
                      <YAxis 
                        yAxisId="btc"
                        orientation="left"
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        domain={btcDomain}
                        width={60}
                      />

                      <YAxis 
                        yAxisId="indicators"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        domain={['auto', 'auto']}
                        width={70}
                        tickFormatter={(v) => {
                          const meta = INDICATOR_UNITS[selectedIndicator];
                          const unit = meta?.unit;
                          if (unit === 'percent') return `${Number(v).toFixed(2)}%`;
                          if (unit === 'index') return Number(v).toFixed(3);
                          if (unit === 'bps') return `${Number(v).toFixed(1)} bps`;
                          // usd
                          const n = Number(v);
                          if (!Number.isFinite(n)) return '';
                          if (Math.abs(n) >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
                          if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
                          if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
                          return `$${n.toFixed(0)}`;
                        }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '12px',
                          fontSize: '12px',
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.3)'
                        }}
                        formatter={(value: any, name: string, props: any) => {
                          if (name === 'BTC') return [`$${Number(value).toLocaleString()}`, name];
                          const unit = INDICATOR_UNITS[selectedIndicator]?.unit;
                          const v = Number(value);
                          if (!Number.isFinite(v)) return ['N/A', name];
                          if (unit === 'percent') return [`${v.toFixed(2)}%`, name];
                          if (unit === 'index') return [v.toFixed(3), name];
                          if (unit === 'bps') return [`${v.toFixed(1)} bps`, name];
                          if (Math.abs(v) >= 1e12) return [`$${(v / 1e12).toFixed(2)}T`, name];
                          if (Math.abs(v) >= 1e9) return [`$${(v / 1e9).toFixed(2)}B`, name];
                          if (Math.abs(v) >= 1e6) return [`$${(v / 1e6).toFixed(2)}M`, name];
                          return [`$${v.toFixed(0)}`, name];
                        }}
                        labelFormatter={(d) => {
                          try {
                            return format(parseISO(d), 'dd MMM yyyy');
                          } catch {
                            return d;
                          }
                        }}
                      />
                      <Legend />
                      
                      {/* BTC Price Area */}
                      <Area
                        yAxisId="btc"
                        type="monotone"
                        dataKey="btcPrice"
                        name="BTC"
                        stroke="hsl(var(--primary))"
                        fill="url(#btcGradient)"
                        strokeWidth={2}
                      />

                      {/* Selected Indicator Line (raw scale) */}
                      <Line
                        yAxisId="indicators"
                        type="monotone"
                        dataKey="indicatorValue"
                        name={INDICATORS[selectedIndicator]?.label}
                        stroke={INDICATORS[selectedIndicator]?.color}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sidebar - Indicator Selection */}
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-xl h-fit">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Indicadores
              </CardTitle>
              <CardDescription className="text-xs">
                Clique para trocar o indicador
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[450px] overflow-y-auto">
              {availableIndicators.map((key) => {
                const ind = INDICATORS[key];
                if (!ind) return null;
                return (
                  <IndicatorToggleRow
                    key={key}
                    indicator={ind}
                    isActive={selectedIndicator === key}
                    onToggle={() => toggleIndicator(key)}
                  />
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
          {availableIndicators.slice(0, 8).map((key, i) => {
            const ind = INDICATORS[key];
            if (!ind) return null;
            const Icon = ind.icon;
            const metric = getMetricValue(key);
            const isSelected = selectedIndicator === key;
            
            const getChangeColor = () => {
              if (metric.changeValue === null || metric.changeValue === undefined) return 'text-muted-foreground';
              if (metric.changeValue > 0) return 'text-emerald-500';
              if (metric.changeValue < 0) return 'text-red-500';
              return 'text-muted-foreground';
            };

            const getChangeIcon = () => {
              if (metric.changeValue === null || metric.changeValue === undefined) return <Minus className="h-3 w-3" />;
              if (metric.changeValue > 0) return <TrendingUp className="h-3 w-3" />;
              if (metric.changeValue < 0) return <TrendingDown className="h-3 w-3" />;
              return <Minus className="h-3 w-3" />;
            };
            
            return (
              <Card 
                key={key}
                className={cn(
                  "border-border/50 bg-card/50 backdrop-blur-sm cursor-pointer transition-all hover:shadow-lg hover:border-primary/30 group",
                  isSelected && "ring-2 ring-offset-2 ring-offset-background"
                )}
                style={{ ['--tw-ring-color' as string]: ind.color }}
                onClick={() => toggleIndicator(key)}
              >
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="h-10 w-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${ind.color}15` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: ind.color }} />
                    </div>
                    {metric.change && (
                      <div className={cn("flex items-center gap-1 text-xs font-medium", getChangeColor())}>
                        {getChangeIcon()}
                        <span>{metric.change}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium truncate">{ind.label}</p>
                    <p className="text-xl font-bold">{metric.value}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Educational Section */}
        {selectedIndicator && (
          <Collapsible open={educationOpen} onOpenChange={setEducationOpen}>
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm animate-fade-in" style={{ animationDelay: '300ms' }}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Entenda os Indicadores Selecionados
                  </CardTitle>
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    educationOpen && "rotate-180"
                  )} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  {(() => {
                    const ind = INDICATORS[selectedIndicator];
                    if (!ind) return null;
                    const Icon = ind.icon;
                    return (
                      <div className="p-4 rounded-xl border border-border/50 bg-muted/30 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${ind.color}20` }}>
                            <Icon className="h-5 w-5" style={{ color: ind.color }} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{ind.fullName}</h3>
                            <p className="text-sm text-muted-foreground">{ind.description}</p>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3 text-sm">
                          <div className="p-3 rounded-lg bg-emerald-500/10 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                              <span className="font-medium text-emerald-500">Subindo</span>
                            </div>
                            <p className="text-muted-foreground text-xs">{ind.rising}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-red-500/10 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                              <span className="font-medium text-red-500">Caindo</span>
                            </div>
                            <p className="text-muted-foreground text-xs">{ind.falling}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-primary/10 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <Activity className="h-3.5 w-3.5 text-primary" />
                              <span className="font-medium text-primary">Correlação BTC</span>
                            </div>
                            <p className="text-muted-foreground text-xs">{ind.correlation}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Last Updated */}
        {macroData?.lastUpdated && (
          <p className="text-xs text-muted-foreground text-center animate-fade-in" style={{ animationDelay: '400ms' }}>
            Última atualização: {format(parseISO(macroData.lastUpdated), 'dd/MM/yyyy HH:mm')}
          </p>
        )}
      </div>
    </div>
  );
}
